/**
 * Shimmering loading skeleton components for IPA portals.
 *
 * Provides three building blocks that match the dashboard grid layout so
 * skeleton → data transitions do not trigger layout shift:
 * - {@link SkeletonText} — a single shimmer bar of configurable width/height.
 * - {@link SkeletonCard} — a card placeholder with a title and body lines.
 * - {@link SkeletonDashboard} — a composite of header + grid of cards.
 *
 * Accessibility: each element exposes `role="status"`, `aria-busy="true"`, and
 * `aria-live="polite"` so assistive technology announces the loading state.
 *
 * The shimmer animation is CSS-driven via a single `@keyframes` block
 * injected once into `document.head` at module load.
 *
 * @module shared/components/Skeleton
 */

import React from 'react';

/** DOM id used to deduplicate the injected shimmer keyframes `<style>` tag. */
const SHIMMER_STYLE_ID = 'ipa-skeleton-shimmer-keyframes';
let shimmerKeyframesInjected = false;

/**
 * Injects the shimmer keyframes `<style>` element into `document.head`
 * exactly once. Safe to call in SSR contexts (no-op when `document` is
 * undefined) and idempotent across multiple module loads.
 */
function ensureShimmerKeyframes(): void {
  if (shimmerKeyframesInjected) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById(SHIMMER_STYLE_ID)) {
    shimmerKeyframesInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = SHIMMER_STYLE_ID;
  style.textContent = `
    @keyframes ipa-skeleton-shimmer {
      0% { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }
  `;
  document.head.appendChild(style);
  shimmerKeyframesInjected = true;
}

ensureShimmerKeyframes();

const shimmerBase: React.CSSProperties = {
  background: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 200px, #e5e7eb 400px)',
  backgroundSize: '800px 100%',
  animation: 'ipa-skeleton-shimmer 1.4s ease-in-out infinite',
  borderRadius: '0.375rem',
};

/** Props for {@link SkeletonText}. */
export interface SkeletonTextProps {
  /** Width of the shimmer bar (CSS length). Defaults to `100%`. */
  width?: string | number;
  /** Height of the shimmer bar (CSS length). Defaults to `1rem`. */
  height?: string | number;
  /** Optional extra className forwarded to the root element. */
  className?: string;
  /** Inline style overrides merged on top of the shimmer defaults. */
  style?: React.CSSProperties;
}

/**
 * A single shimmering bar used to represent a line of text that is loading.
 *
 * @param props - Size and style overrides.
 * @returns An accessible shimmer element.
 */
export function SkeletonText({
  width = '100%',
  height = '1rem',
  className,
  style,
}: SkeletonTextProps): React.JSX.Element {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={className}
      data-testid="skeleton-text"
      style={{ ...shimmerBase, width, height, ...style }}
    />
  );
}

/** Props for {@link SkeletonCard}. */
export interface SkeletonCardProps {
  /** Optional extra className forwarded to the root element. */
  className?: string;
  /** Inline style overrides merged on top of the card defaults. */
  style?: React.CSSProperties;
  /** Number of body shimmer lines to render below the title. Defaults to 3. */
  lines?: number;
}

/**
 * A card-shaped placeholder that matches the dashboard card dimensions.
 *
 * Renders a title shimmer plus `lines` body shimmers. The last line is
 * rendered at 40% width to mimic a natural paragraph shape.
 *
 * @param props - Card size and body line count overrides.
 * @returns An accessible card shimmer element.
 */
export function SkeletonCard({
  className,
  style,
  lines = 3,
}: SkeletonCardProps): React.JSX.Element {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      aria-label="Loading card"
      className={className}
      data-testid="skeleton-card"
      style={{
        padding: '1rem',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb',
        background: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        minHeight: '160px',
        ...style,
      }}
    >
      <SkeletonText height="1.25rem" width="60%" />
      {Array.from({ length: lines }).map((_, idx) => (
        <SkeletonText key={idx} height="0.75rem" width={idx === lines - 1 ? '40%' : '90%'} />
      ))}
    </div>
  );
}

/** Props for {@link SkeletonDashboard}. */
export interface SkeletonDashboardProps {
  /** Number of skeleton cards to render in the grid. Defaults to 3. */
  cards?: number;
  /** Override the root `data-testid`. Defaults to `"skeleton-dashboard"`. */
  'data-testid'?: string;
}

/**
 * A composite dashboard placeholder with a header shimmer and a responsive
 * grid of {@link SkeletonCard}. Dimensions (max-width, grid template) are
 * aligned with the subscriber dashboard grid so the skeleton → data
 * transition does not cause a layout shift.
 *
 * @param props - Grid size and testid overrides.
 * @returns The dashboard skeleton React element.
 */
export function SkeletonDashboard({
  cards = 3,
  'data-testid': testId = 'skeleton-dashboard',
}: SkeletonDashboardProps): React.JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        maxWidth: '1024px',
        margin: '0 auto',
        padding: '1rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <SkeletonText height="1.5rem" width="200px" />
        <SkeletonText height="1.5rem" width="40px" style={{ borderRadius: '9999px' }} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: '1rem',
        }}
      >
        {Array.from({ length: cards }).map((_, idx) => (
          <SkeletonCard key={idx} />
        ))}
      </div>
    </div>
  );
}
